import React, { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import http from '../api/http';
import { resolveApiUrl } from '../api/http';
import { useAuth } from '../context/AuthContext';

export default function AccessCardManagementPage() {
  const auth = useAuth();
  const canManage = auth.hasPermission(['manage_access_cards']);

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [cardColor, setCardColor] = useState('');
  // 颜色无需筛选暂不加筛选UI，如需可添加 cardColor 状态
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]); // 当前下拉候选
  const [empQuery, setEmpQuery] = useState('');
  const [empLoading, setEmpLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record or null
  const [selectedIds, setSelectedIds] = useState([]);
  // 导入相关状态
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState('upsert'); // upsert | skip
  const [importUploading, setImportUploading] = useState(false);
  const [importResult, setImportResult] = useState(null); // { total, inserted, updated, skipped, errors }
  const [importFile, setImportFile] = useState(null);
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);
  const [swapNote, setSwapNote] = useState('');
  const [swapColor, setSwapColor] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [resolvedHolderName, setResolvedHolderName] = useState('');
  const [recentStats, setRecentStats] = useState(null);
  const [recentStatsLoading, setRecentStatsLoading] = useState(false);
  const [recentStatsError, setRecentStatsError] = useState('');
  const [pendingInterns, setPendingInterns] = useState([]);
  const [pendingResignedCards, setPendingResignedCards] = useState([]);
  const [pendingActionsLoading, setPendingActionsLoading] = useState(false);
  const [pendingActionsError, setPendingActionsError] = useState('');

  const yellowCards = useMemo(() => list.filter(item => (item.card_color || '').trim() === '黄'), [list]);
  const redCards = useMemo(() => list.filter(item => (item.card_color || '').trim() === '红'), [list]);

  const recoveredThisMonth = useMemo(() => {
    const start = dayjs().startOf('month');
    const end = dayjs().endOf('month');
    return (list || []).filter(r => {
      if (String(r?.status || '').trim() !== '回收') return false;
      if (!r?.recovered_at) return false;
      const d = dayjs(r.recovered_at);
      if (!d.isValid()) return false;
      return (d.isAfter(start) || d.isSame(start, 'day')) && (d.isBefore(end) || d.isSame(end, 'day'));
    });
  }, [list]);

  const recoveredThisMonthNames = useMemo(() => {
    const names = (recoveredThisMonth || [])
      .map(r => (r?.employee_name || r?.holder_name || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [recoveredThisMonth]);
  const renderColorBookmark = useCallback((label, entries, badgeClass) => {
    if (!entries.length) return null;
    return (
      <div key={label} style={{ minWidth: 180, padding: 8, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className={badgeClass}>{label}</span>
          <span style={{ fontSize: 13, color: '#4a5568' }}>{entries.length} 人</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map(entry => {
            const name = entry.employee_name || entry.holder_name || '-';
            return (
              <a key={entry.id} href={`#access-card-${entry.id}`} style={{ fontSize: 13, color: '#1a73e8', textDecoration: 'none' }}>
                {name}
              </a>
            );
          })}
        </div>
      </div>
    );
  }, []);

  const colorSummaryEntries = useMemo(() => {
    if (!recentStats || !recentStats.colorCounts) return [];
    const counts = recentStats.colorCounts;
    const preferredOrder = ['绿', '黄', '红', '未设置'];
    const entries = [];
    const seen = new Set();
    for (const color of preferredOrder) {
      const value = counts[color] ?? 0;
      entries.push([color, value]);
      seen.add(color);
    }
    for (const [color, count] of Object.entries(counts)) {
      if (seen.has(color)) continue;
      entries.push([color, count]);
    }
    return entries;
  }, [recentStats]);

  const fetchCards = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (keyword) params.keyword = keyword;
      if (status) params.status = status;
      if (cardColor) params.card_color = cardColor;
      const { data } = await http.get('/access-cards', { params });
      setList(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [keyword, status, cardColor]);

  const fetchRecentStats = useCallback(async () => {
    try {
      setRecentStatsLoading(true);
      setRecentStatsError('');
      const { data } = await http.get('/access-cards/stats/recent');
      setRecentStats(data);
    } catch (err) {
      setRecentStats(null);
      setRecentStatsError(err?.response?.data?.error || err.message);
    } finally {
      setRecentStatsLoading(false);
    }
  }, []);

  const fetchPendingActions = useCallback(async () => {
    try {
      setPendingActionsLoading(true);
      setPendingActionsError('');
      const { data } = await http.get('/access-cards/stats/pending-actions');
      setPendingInterns(Array.isArray(data?.internsWithoutCard) ? data.internsWithoutCard : []);
      setPendingResignedCards(Array.isArray(data?.resignedPendingRecovery) ? data.resignedPendingRecovery : []);
    } catch (err) {
      setPendingInterns([]);
      setPendingResignedCards([]);
      setPendingActionsError(err?.response?.data?.error || err.message);
    } finally {
      setPendingActionsLoading(false);
    }
  }, []);

  // 模糊搜索员工（姓名包含）
  const fetchEmployees = useCallback(async (q) => {
    try {
      setEmpLoading(true);
      const keyword = q ? q.trim() : '';
      const [mainRes, internRes] = await Promise.allSettled([
        http.get('/employees', { params: keyword ? { name: keyword } : {} }),
        http.get('/employees', { params: keyword ? { name: keyword, category: 'intern' } : { category: 'intern' } })
      ]);

      const mainList = mainRes.status === 'fulfilled' && Array.isArray(mainRes.value.data)
        ? mainRes.value.data.map(emp => ({
            ...emp,
            record_source: emp.record_source || 'main',
            displayName: `${emp.name}${emp.department ? ` - ${emp.department}${emp.section ? '/' + emp.section : ''}` : ''}`
          }))
        : [];
      const internList = internRes.status === 'fulfilled' && Array.isArray(internRes.value.data)
        ? internRes.value.data.map(emp => ({
            ...emp,
            record_source: 'intern',
            displayName: `[实习] ${emp.name}${emp.department ? ` - ${emp.department}${emp.section ? '/' + emp.section : ''}` : ''}`
          }))
        : [];

      const combined = [...mainList, ...internList].slice(0, 50);
      setEmployees(combined);
    } catch (err) {
      console.error('门禁卡员工检索失败', err);
      setEmployees([]);
    } finally {
      setEmpLoading(false);
    }
  }, []);

  useEffect(() => { fetchCards(); }, [fetchCards]);
  useEffect(() => { fetchRecentStats(); }, [fetchRecentStats]);
  useEffect(() => { fetchPendingActions(); }, [fetchPendingActions]);
  useEffect(() => {
    if (!modalOpen) return;
    fetchEmployees('');
    if (editing) {
      setEmpQuery(editing.employee_name || editing.holder_name || '');
      setSelectedEmployeeId(editing.employee_id ? String(editing.employee_id) : '');
      setResolvedHolderName(editing.employee_name || editing.holder_name || '');
    } else {
      setEmpQuery('');
      setSelectedEmployeeId('');
      setResolvedHolderName('');
    }
  }, [modalOpen, fetchEmployees, editing]);

  useEffect(() => {
    if (!modalOpen || !selectedEmployeeId) return;
    const matched = employees.find(emp => String(emp.id) === String(selectedEmployeeId));
    if (matched && matched.record_source !== 'intern') {
      if (matched.displayName && matched.displayName !== empQuery) {
        setEmpQuery(matched.displayName);
      }
      if (matched.name && matched.name !== resolvedHolderName) {
        setResolvedHolderName(matched.name);
      }
    }
  }, [modalOpen, employees, selectedEmployeeId, empQuery, resolvedHolderName]);

  // 输入防抖
  useEffect(() => {
    if (!modalOpen) return;
    const searchTerm = empQuery
      ? empQuery.replace(/^\[实习\]\s*/, '').split(' - ')[0].trim()
      : '';
    const h = setTimeout(() => { fetchEmployees(searchTerm); }, 350);
    return () => clearTimeout(h);
  }, [empQuery, modalOpen, fetchEmployees]);

  const resetForm = () => { setEditing(null); setEmpQuery(''); setSelectedEmployeeId(''); setResolvedHolderName(''); };
  const openCreate = () => { resetForm(); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setModalOpen(true); };
  const openImport = () => { setImportResult(null); setImportFile(null); setImportMode('upsert'); setImportModalOpen(true); };
  const openSwap = (record) => {
    setSwapTarget(record);
    setSwapColor(record?.card_color || '');
    setSwapNote('');
    setSwapModalOpen(true);
  };

  const toggleSelect = (id, checked) => {
    setSelectedIds(prev => checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
  };
  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(list.map(r => r.id));
    } else setSelectedIds([]);
  };

  const batchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`确认删除选中的 ${selectedIds.length} 条记录?`)) return;
    for (const id of selectedIds) {
      await http.delete(`/access-cards/${id}`);
    }
    setSelectedIds([]);
    fetchCards();
    fetchRecentStats();
    fetchPendingActions();
  };
  const batchRecover = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`确认回收选中的 ${selectedIds.length} 条记录?`)) return;
    for (const id of selectedIds) {
      await http.post(`/access-cards/${id}/recover`);
    }
    setSelectedIds([]);
    fetchCards();
    fetchRecentStats();
    fetchPendingActions();
  };

  const exportCSV = async () => {
    try {
      const params = {};
      if (keyword) params.keyword = keyword;
      if (status) params.status = status;
      params.format = 'csv';
      const query = new URLSearchParams(params).toString();
      const resp = await fetch(resolveApiUrl(`/api/access-cards/export?${query}`), { headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }});
      if (!resp.ok) throw new Error('导出失败');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `access_cards_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    }
  };

  // 轻量 Excel - 仍使用前端当前数据（可扩展为后端）
  const exportExcel = () => {
    const tableHead = `<tr><th>ID</th><th>卡号</th><th>持卡人</th><th>状态</th><th>发卡日期</th><th>回收日期</th><th>备注</th></tr>`;
    const tableBody = list.map(r => `<tr><td>${r.id}</td><td>${r.card_number}</td><td>${r.holder_name || r.employee_name || ''}</td><td>${r.status}</td><td>${r.issued_at || ''}</td><td>${r.recovered_at || ''}</td><td>${(r.note||'').replace(/\n/g,' ')}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset='utf-8' /></head><body><table>${tableHead}${tableBody}</table></body></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `access_cards_partial_${Date.now()}.xls`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleRecover = async (r) => {
    if (!window.confirm('确认回收该门禁卡?')) return;
    await http.post(`/access-cards/${r.id}/recover`);
    fetchCards();
    fetchRecentStats();
    fetchPendingActions();
  };

  const handleSwapSubmit = async (e) => {
    e.preventDefault();
    if (!swapTarget) return;
    if (!swapColor) {
      alert('请选择新的卡颜色');
      return;
    }
    if (swapColor === swapTarget.card_color) {
      alert('新卡颜色需要不同于当前颜色');
      return;
    }
    setSwapping(true);
    try {
      const payload = { card_color: swapColor };
      if (swapNote.trim()) payload.note = `${swapTarget.note ? `${swapTarget.note}\n` : ''}[换卡] ${swapNote.trim()}`;
      await http.put(`/access-cards/${swapTarget.id}`, payload);
      setSwapModalOpen(false);
      setSwapTarget(null);
      fetchCards();
      fetchRecentStats();
      fetchPendingActions();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setSwapping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const payload = Object.fromEntries(form.entries());
    const displayValue = payload.holder_display;
    delete payload.holder_display;
    if (!payload.holder_name && typeof displayValue === 'string') {
      payload.holder_name = displayValue.trim();
    }
    try {
      if (editing) {
        await http.put(`/access-cards/${editing.id}`, payload);
      } else {
        await http.post('/access-cards', payload);
      }
      setModalOpen(false);
      fetchCards();
      fetchRecentStats();
      fetchPendingActions();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <h2 className="panel-title">门禁卡管理</h2>
        <div className="spacer" />
        {canManage && (
          <>
            <button
              className="btn btn-danger"
              type="button"
              onClick={batchDelete}
              disabled={selectedIds.length === 0}
              title={selectedIds.length === 0 ? '请先勾选要删除的门禁卡' : `删除选中的 ${selectedIds.length} 条`}
            >
              删除选中
            </button>
            <button className="btn" onClick={openCreate}>新增门禁卡</button>
            <button className="btn btn-secondary" style={{marginLeft:8}} onClick={openImport}>批量导入</button>
          </>
        )}
      </div>

      <div className="summary-strip" style={{marginBottom:12}}>
        {recentStatsLoading && (
          <div className="muted" style={{fontSize:13}}>最近三个月数据加载中...</div>
        )}
        {!recentStatsLoading && recentStatsError && (
          <div className="text-danger" style={{fontSize:13}}>{recentStatsError}</div>
        )}
        {!recentStatsLoading && !recentStatsError && recentStats && (
          <div style={{display:'flex', flexWrap:'wrap', gap:16, padding:12, border:'1px solid #e2e8f0', borderRadius:6, background:'#fafafa'}}>
            <div style={{minWidth:160}}>
              <div className="muted" style={{fontSize:12}}>最近3个月新增</div>
              <div style={{fontSize:20, fontWeight:600}}>{recentStats.recentCount || 0} 张</div>
            </div>
            <div style={{minWidth:160}}>
              <div className="muted" style={{fontSize:12}}>最大卡号</div>
              <div style={{fontSize:20, fontWeight:600}}>{recentStats.maxCardNumber || '-'}</div>
            </div>

            <div style={{minWidth:220}}>
              <div className="muted" style={{fontSize:12}}>本月已回收</div>
              <div style={{fontSize:20, fontWeight:600}}>{recoveredThisMonth.length} 张</div>
              <div className="muted" style={{fontSize:12, marginTop:4}}>
                {recoveredThisMonthNames.length ? (
                  <details>
                    <summary style={{cursor:'pointer'}}>查看名单 ({recoveredThisMonthNames.length})</summary>
                    <div style={{marginTop:6, display:'flex', flexWrap:'wrap', gap:6}}>
                      {recoveredThisMonthNames.map(n => (
                        <span key={n} className="badge badge-neutral" style={{fontSize:12}}>{n}</span>
                      ))}
                    </div>
                  </details>
                ) : (
                  <span>—</span>
                )}
              </div>
            </div>
            {colorSummaryEntries.length > 0 && (
              <div style={{display:'flex', flexWrap:'wrap', gap:8, alignItems:'center'}}>
                <span className="muted" style={{fontSize:12}}>按颜色统计：</span>
                {colorSummaryEntries.map(([color, count]) => {
                  const badgeClass = color === '绿'
                    ? 'badge badge-success'
                    : color === '黄'
                      ? 'badge badge-warning'
                      : color === '红'
                        ? 'badge badge-danger'
                        : 'badge';
                  const label = color === '未设置' ? '未设置' : `${color}卡`;
                  return (
                    <span key={color} className={badgeClass} style={{fontSize:12}}>
                      {label} {count}
                    </span>
                  );
                })}
              </div>
            )}
            {recentStats.recentCards?.length > 0 && (
              <details style={{flexBasis:'100%', marginTop:4}}>
                <summary style={{cursor:'pointer', fontSize:13}}>查看最近新增 ({recentStats.recentCards.length})</summary>
                <ul style={{marginTop:8, paddingLeft:16, fontSize:13, lineHeight:1.5}}>
                  {recentStats.recentCards.map(card => (
                    <li key={card.id}>
                      {card.card_number || '-'} - {(card.employee_name || card.holder_name || '-')}{card.created_at ? ` - ${card.created_at}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {(yellowCards.length > 0 || redCards.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, flexBasis: '100%' }}>
                {renderColorBookmark('黄卡人员', yellowCards, 'badge badge-warning')}
                {renderColorBookmark('红卡人员', redCards, 'badge badge-danger')}
              </div>
            )}
            <div style={{flexBasis:'100%', marginTop:8}}>
              {pendingActionsLoading && (
                <div className="muted" style={{fontSize:13}}>校验实习/离职人员门禁状态中...</div>
              )}
              {!pendingActionsLoading && pendingActionsError && (
                <div className="text-danger" style={{fontSize:13}}>{pendingActionsError}</div>
              )}
              {!pendingActionsLoading && !pendingActionsError && (
                <div style={{display:'flex', flexWrap:'wrap', gap:12}}>
                  {[{
                    title: '未办卡实习人员',
                    count: pendingInterns.length,
                    badgeClass: 'badge-warning',
                    emptyText: '全部实习人员已登记门禁卡。',
                    highlightColor: '#facc15',
                    items: pendingInterns,
                    renderItem: (item) => (
                      <span>
                        {item.name}
                        {item.department ? ` - ${item.department}${item.section ? '/' + item.section : ''}` : ''}
                        {item.position ? ` (${item.position})` : ''}
                      </span>
                    )
                  }, {
                    title: '离职未回收门禁卡',
                    count: pendingResignedCards.length,
                    badgeClass: 'badge-danger',
                    emptyText: '离职人员门禁卡均已回收。',
                    highlightColor: '#f87171',
                    items: pendingResignedCards,
                    renderItem: (item) => (
                      <span>
                        <a href={`#access-card-${item.card_id}`} style={{color:'#1a73e8', textDecoration:'none'}}>
                          {item.name || item.holder_name || '-'}
                        </a>
                        {item.card_number ? ` · 卡号 ${item.card_number}` : ''}
                        {item.resigned_date ? ` · 离职 ${item.resigned_date}` : ''}
                        {item.status && item.status !== '在用' ? ` · 状态 ${item.status}` : ''}
                      </span>
                    )
                  }].map(block => (
                    <div
                      key={block.title}
                      style={{
                        minWidth: 220,
                        flex: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 14,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        inset: '12px',
                        borderRadius: 12,
                        background: `linear-gradient(135deg, ${block.highlightColor}22 0%, #ffffff 65%)`,
                        pointerEvents: 'none'
                      }} />
                      <div style={{ position: 'relative', padding: '16px 18px 18px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{block.title}</span>
                          <span className={`badge ${block.badgeClass}`} style={{ fontSize: 11, padding: '2px 10px', borderRadius: 999 }}>{block.count} 人</span>
                        </div>
                        {block.count === 0 ? (
                          <div className="muted" style={{ fontSize: 12 }}>{block.emptyText}</div>
                        ) : (
                          <ul style={{
                            margin: 0,
                            paddingLeft: 16,
                            fontSize: 12,
                            lineHeight: 1.6,
                            maxHeight: 200,
                            overflowY: 'auto',
                            color: '#0f172a'
                          }}>
                            {block.items.map(item => (
                              <li key={`${block.title}-${item.card_id || item.id}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: block.highlightColor, flexShrink: 0 }} />
                                {block.renderItem(item)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="toolbar">
        <input placeholder="搜索 卡号/姓名" value={keyword} onChange={e=>setKeyword(e.target.value)} />
        <select value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="在用">在用</option>
          <option value="回收">回收</option>
          <option value="挂失">挂失</option>
          <option value="停用">停用</option>
        </select>
        <select value={cardColor} onChange={e=>setCardColor(e.target.value)}>
          <option value="">全部颜色</option>
          <option value="绿">绿卡</option>
          <option value="黄">黄卡</option>
          <option value="红">红卡</option>
        </select>
        <button className="btn btn-secondary" onClick={fetchCards}>查询</button>
        <div className="spacer" />
        <button type="button" className="btn btn-outline btn-small" onClick={exportCSV}>导出 CSV</button>
        <button type="button" className="btn btn-outline btn-small" onClick={exportExcel}>导出 Excel</button>
        {canManage && selectedIds.length > 0 && (
          <>
            <button type="button" className="btn btn-secondary btn-small" onClick={batchRecover}>批量回收</button>
          </>
        )}
      </div>

      {loading && <div className="muted" style={{padding:'6px 2px'}}>加载中...</div>}
      {error && <div className="text-danger" style={{padding:'6px 2px'}}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 12px 0' }}>
        {canManage && (
          <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              onChange={toggleSelectAll}
              checked={selectedIds.length > 0 && selectedIds.length === list.length && list.length > 0}
            />
            全选（{selectedIds.length}/{list.length}）
          </label>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 12
        }}
      >
        {list.map(r => {
          const holder = r.holder_name || r.employee_name || '-';
          const statusTxt = r.status || '-';
          const statusBadge = statusTxt === '在用'
            ? 'badge-success'
            : statusTxt === '回收'
              ? 'badge-neutral'
              : statusTxt === '挂失'
                ? 'badge-danger'
                : 'badge-warning';
          const colorBadge = r.card_color === '绿'
            ? 'badge-success'
            : r.card_color === '黄'
              ? 'badge-warning'
              : r.card_color === '红'
                ? 'badge-danger'
                : 'badge-neutral';

          return (
            <div
              key={r.id}
              id={`access-card-${r.id}`}
              style={{
                border: selectedIds.includes(r.id) ? '1px solid #2563eb' : '1px solid #e2e8f0',
                borderRadius: 12,
                background: '#fff',
                padding: 12,
                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{r.card_number || '-'}</div>
                    <span className={`badge ${statusBadge}`} style={{ fontSize: 12 }}>{statusTxt}</span>
                    {r.card_color ? (
                      <span className={`badge ${colorBadge}`} style={{ fontSize: 12 }}>{r.card_color}卡</span>
                    ) : (
                      <span className="badge badge-neutral" style={{ fontSize: 12 }}>未标记</span>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    持卡人：{holder}
                  </div>
                  {r.department && (
                    <div className="muted" style={{ marginTop: 4, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      部门：{r.department}
                    </div>
                  )}
                </div>

                {canManage && (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={e => toggleSelect(r.id, e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                <div>
                  <div className="muted" style={{ fontSize: 12 }}>发卡日期</div>
                  <div style={{ fontSize: 13 }}>{r.issued_at || '-'}</div>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 12 }}>回收日期</div>
                  <div style={{ fontSize: 13 }}>{r.recovered_at || '-'}</div>
                </div>
              </div>

              {r.note ? (
                <div style={{ marginTop: 10 }}>
                  <div className="muted" style={{ fontSize: 12 }}>备注</div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#0f172a' }}>{r.note}</div>
                </div>
              ) : null}

              {canManage && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
                  <button className="btn btn-outline btn-small" type="button" onClick={() => openEdit(r)}>编辑</button>
                  <button className="btn btn-outline btn-small" type="button" onClick={() => openSwap(r)}>换卡</button>
                  {r.status !== '回收' && (
                    <button className="btn btn-secondary btn-small" type="button" onClick={() => handleRecover(r)}>回收</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {list.length === 0 && !loading && (
        <div className="muted" style={{ textAlign: 'center', padding: 24 }}>暂无数据</div>
      )}

      {modalOpen && (
        <div className="modal-backdrop">
          <form onSubmit={handleSubmit} className="modal" autoComplete="off">
            <h3>{editing ? '编辑门禁卡' : '新增门禁卡'}</h3>
            <label className="form-field">
              <span>卡号 *</span>
              <input name="card_number" defaultValue={editing?.card_number || ''} required disabled={!!editing} />
            </label>
            <label className="form-field">
              <span>关联员工 / 外部姓名</span>
              <div style={{display:'flex', flexDirection:'column', gap:6}}>
                <input
                  type="text"
                  name="holder_display"
                  placeholder="输入姓名关键字检索"
                  value={empQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    const normalized = value.trim();
                    const match = employees.find(emp => emp.displayName === normalized || emp.displayName === value || emp.name === normalized);
                    if (match) {
                      setEmpQuery(match.displayName);
                      if (match.record_source !== 'intern') {
                        setSelectedEmployeeId(String(match.id));
                      } else {
                        setSelectedEmployeeId('');
                      }
                      setResolvedHolderName(match.name);
                    } else {
                      setEmpQuery(value);
                      setSelectedEmployeeId('');
                      setResolvedHolderName(normalized);
                    }
                  }}
                  list="access-card-employee-suggestions"
                  style={{padding:'6px 10px'}}
                />
                <datalist id="access-card-employee-suggestions">
                  {empLoading && <option value="" label="加载中..." />}
                  {employees.map(emp => (
                    <option
                      key={`${emp.record_source}-${emp.id}`}
                      value={emp.displayName}
                      label={emp.displayName}
                    />
                  ))}
                  {(!empLoading && employees.length === 0) && <option value="" label="无匹配结果" />}
                </datalist>
                <input type="hidden" name="holder_name" value={resolvedHolderName} />
                <input type="hidden" name="employee_id" value={selectedEmployeeId} />
                <div className="muted" style={{fontSize:12}}>
                  说明：直接输入姓名可保存为外部人员；从建议列表中选择将自动关联内部员工。
                </div>
              </div>
            </label>
            <label className="form-field">
              <span>发卡日期</span>
              <input type="date" name="issued_at" defaultValue={editing?.issued_at || ''} />
            </label>
            <label className="form-field">
              <span>卡颜色</span>
              <select name="card_color" defaultValue={editing?.card_color || ''}>
                <option value="">(自动推断)</option>
                <option value="绿">绿</option>
                <option value="黄">黄</option>
                <option value="红">红</option>
              </select>
            </label>
            {editing && (
              <label className="form-field">
                <span>状态</span>
                <select name="status" defaultValue={editing.status}>
                  <option value="在用">在用</option>
                  <option value="回收">回收</option>
                  <option value="挂失">挂失</option>
                  <option value="停用">停用</option>
                </select>
              </label>
            )}
            {editing && (
              <label className="form-field">
                <span>回收日期</span>
                <input type="date" name="recovered_at" defaultValue={editing?.recovered_at || ''} />
              </label>
            )}
            <label className="form-field">
              <span>备注</span>
              <textarea name="note" rows={3} defaultValue={editing?.note || ''} />
            </label>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>取消</button>
              <button type="submit" className="btn">保存</button>
            </div>
          </form>
        </div>
      )}
      {importModalOpen && (
        <div className="modal-backdrop">
          <div className="modal" style={{maxWidth:600}}>
            <h3>批量导入门禁卡</h3>
            <div className="muted" style={{fontSize:13, lineHeight:1.4, marginBottom:8}}>
              支持 Excel 或 CSV；识别列：卡号/门禁卡号、姓名/持卡人、状态、发卡日期、回收日期、卡颜色、备注。<br />
              模式说明：Upsert 会更新已存在卡号（只覆盖非空字段），Skip 跳过已存在卡号。
            </div>
            <label className="form-field">
              <span>选择文件 *</span>
              <input type="file" accept=".xls,.xlsx,.csv" onChange={e=>setImportFile(e.target.files?.[0]||null)} />
            </label>
            <label className="form-field">
              <span>导入模式</span>
              <select value={importMode} onChange={e=>setImportMode(e.target.value)}>
                <option value="upsert">Upsert (更新或新增)</option>
                <option value="skip">Skip (跳过已存在)</option>
              </select>
            </label>
            <div className="actions" style={{justifyContent:'space-between'}}>
              <div style={{display:'flex', gap:8}}>
                <button className="btn btn-secondary" type="button" onClick={()=>setImportModalOpen(false)} disabled={importUploading}>关闭</button>
                {importResult && (
                  <button type="button" className="btn btn-outline" onClick={()=>setImportResult(null)} disabled={importUploading}>清空结果</button>
                )}
              </div>
              <button className="btn" type="button" disabled={!importFile || importUploading} onClick={async ()=>{
                if (!importFile) return;
                setImportUploading(true); setImportResult(null);
                try {
                  const fd = new FormData(); fd.append('file', importFile);
                  const { data } = await http.post(`/access-cards/import?mode=${importMode}`, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
                  setImportResult(data);
                  // 自动刷新列表
                  fetchCards();
                  fetchRecentStats();
                  fetchPendingActions();
                } catch (e) {
                  alert(e?.response?.data?.message || e.message);
                } finally { setImportUploading(false); }
              }}>{importUploading ? '上传中...' : '开始导入'}</button>
            </div>
            {importResult && (
              <div style={{marginTop:12}}>
                <h4 style={{margin:'8px 0'}}>导入结果</h4>
                {'code' in importResult && importResult.code !== 0 && (
                  <div className="text-danger">处理失败: {importResult.message}</div>
                )}
                {importResult.code === 0 && (
                  <div className="result-box" style={{display:'flex', flexDirection:'column', gap:6, fontSize:13}}>
                    <div>批次: {importResult.import_batch}</div>
                    <div>总行数: {importResult.total}</div>
                    <div>新增: <b style={{color:'#16a34a'}}>{importResult.inserted}</b> / 更新: <b style={{color:'#2563eb'}}>{importResult.updated}</b> / 跳过: <b style={{color:'#64748b'}}>{importResult.skipped}</b></div>
                    {importResult.errors?.length > 0 && (
                      <details style={{marginTop:4}}>
                        <summary>错误行 ({importResult.errors.length})</summary>
                        <div style={{maxHeight:160, overflow:'auto', background:'#f8fafc', padding:8, border:'1px solid #e2e8f0'}}>
                          {importResult.errors.map((e,i)=>(
                            <div key={i} style={{marginBottom:4}}>
                              <div style={{fontWeight:600}}>Row {i+1}</div>
                              <div style={{whiteSpace:'pre-wrap', fontFamily:'monospace'}}>{JSON.stringify(e.row)}</div>
                              <div className="text-danger">{e.error}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {swapModalOpen && swapTarget && (
        <div className="modal-backdrop">
          <form className="modal" style={{maxWidth:420}} onSubmit={handleSwapSubmit}>
            <h3>换卡流程</h3>
            <div className="muted" style={{fontSize:13, lineHeight:1.5, marginBottom:12}}>
              当前卡号：<strong>{swapTarget.card_number}</strong>（{swapTarget.card_color || '未标记'}）。请选择新的卡颜色并填写换卡说明。
            </div>
            <label className="form-field">
              <span>新卡颜色 *</span>
              <select value={swapColor} onChange={e => setSwapColor(e.target.value)} required>
                <option value="">请选择颜色</option>
                <option value="绿">绿</option>
                <option value="黄">黄</option>
                <option value="红">红</option>
              </select>
            </label>
            <label className="form-field">
              <span>换卡说明</span>
              <textarea value={swapNote} onChange={e => setSwapNote(e.target.value)} rows={3} placeholder="例如：员工岗位调整，改发黄卡" />
            </label>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setSwapModalOpen(false); setSwapTarget(null); }}>取消</button>
              <button type="submit" className="btn" disabled={swapping}>{swapping ? '处理中...' : '确认换卡'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
